import { Link } from "react-router-dom";
import { AlertTriangle, Info, AlertCircle, HelpCircle } from "lucide-react";
import type { AttentionItem } from "../dashboard-selectors";

const ICON = {
  danger: AlertCircle,
  warning: AlertTriangle,
  info: Info,
} as const;

/**
 * `hasError` is true when the approvals or notifications call this bar's
 * counts are built from failed — in that case we must not claim
 * "all systems operational", because the truth is "unknown", not "clear"
 * (ALL-4747 T7 fail-closed rule).
 */
export function AttentionBar({ items, hasError, includeApprovals = true }: { items: AttentionItem[]; hasError: boolean; includeApprovals?: boolean }) {
  if (hasError && items.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-line bg-surface px-4 py-2.5 text-xs text-muted">
        <HelpCircle className="size-3.5" aria-hidden />
        Attention status unavailable — one or more sources failed to load.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-success-border bg-success-soft px-4 py-2.5 text-xs text-success">
        <span className="inline-block size-2 rounded-full bg-success" />
        {includeApprovals ? "No pending approvals or unread notifications." : "No unread notifications."}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface px-4 py-3">
      <span className="text-xs font-semibold uppercase tracking-[0.05em] text-muted">Attention:</span>
      {items.map((item, idx) => {
        const Icon = ICON[item.tone];
        return (
          <span key={item.text} className="inline-flex items-center gap-1.5 text-xs">
            <Icon className={`size-3.5 ${item.tone === "danger" ? "text-danger" : item.tone === "warning" ? "text-warning" : "text-info"}`} />
            <Link to={item.href} className="font-semibold text-ink hover:underline">
              {item.text}
            </Link>
            {idx < items.length - 1 && <span className="text-muted">·</span>}
          </span>
        );
      })}
    </div>
  );
}
